/*----------------------------------
- DIRECT PRODUCT REQUEST
----------------------------------*/

import { fromJson as errorFromJson, type TJsonError } from '@common/errors';
import type { TApiFetchOptions, TFetcherArgs, TPostDataWithFile } from '@common/router/request/api';
import type { CurrentUserSession } from '@uniquedomains/components/contracts/users/session';
import { resolveProductOrigin } from './productOrigin';

/*----------------------------------
- CONSTANTS
----------------------------------*/

const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;
const SHORT_REQUEST_TIMEOUT_MS = 15_000;
const SHORT_REQUEST_PATHS = new Set([
    '/api/Auth/CurrentUser',
    '/api/Auth/Session',
    '/api/Onboarding/GetProgress',
    '/api/Onboarding/SaveProgress',
]);

/*----------------------------------
- HELPERS
----------------------------------*/

const isJsonError = (value: ProteumValue): value is TJsonError => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const candidate = value as Partial<TJsonError>;
    return typeof candidate.code === 'number' && typeof candidate.message === 'string';
};

const describeResponseStatus = (response: Response): string => {
    return response.statusText ? `${response.status} ${response.statusText}` : String(response.status);
};

const readJsonResponse = async (response: Response): Promise<ProteumValue> => {
    const body = await response.text();
    if (!body) {
        if (response.status === 204) return undefined;
        throw new Error(`The product returned an empty JSON response (${describeResponseStatus(response)}).`);
    }

    try {
        return JSON.parse(body) as ProteumValue;
    } catch (cause) {
        const error = new Error(
            `The product returned an invalid JSON response (${describeResponseStatus(response)}).`,
        ) as Error & { cause?: ProteumValue };
        error.cause = cause as ProteumValue;
        throw error;
    }
};

/*----------------------------------
- PUBLIC API
----------------------------------*/

export const executeDirectProductRequest = async <TData,>(
    productOrigin: string,
    method: TFetcherArgs[0],
    path: string,
    data?: TPostDataWithFile,
    options?: TApiFetchOptions,
): Promise<TData> => {
    const url = new URL(path, resolveProductOrigin(productOrigin));
    const headers = new Headers({ Accept: 'application/json' });
    const abortController = new AbortController();
    const config: RequestInit = { method, headers, credentials: 'include', signal: abortController.signal };

    if (data && method === 'GET') {
        for (const [key, value] of Object.entries(data)) {
            if (value === undefined || value === null) continue;
            url.searchParams.set(key, String(value));
        }
    } else if (data) {
        if (options?.encoding === 'multipart') {
            throw new Error('Multipart product controller calls are not supported by the website auth flow.');
        }
        headers.set('Content-Type', 'application/json');
        config.body = JSON.stringify(data);
    }

    const timeoutMs = SHORT_REQUEST_PATHS.has(url.pathname) ? SHORT_REQUEST_TIMEOUT_MS : DEFAULT_REQUEST_TIMEOUT_MS;
    const timeoutId = window.setTimeout(() => abortController.abort(), timeoutMs);
    try {
        const response = await fetch(url.toString(), config);
        const json = await readJsonResponse(response);
        if (!response.ok) {
            if (isJsonError(json)) throw errorFromJson(json);
            throw new Error(`The product request failed (${describeResponseStatus(response)}).`);
        }
        return json as TData;
    } catch (error) {
        if (abortController.signal.aborted) {
            const timeoutError = new Error(
                `The product request timed out after ${Math.round(timeoutMs / 1_000)} seconds.`,
            ) as Error & { cause?: ProteumValue };
            timeoutError.cause = error as ProteumValue;
            throw timeoutError;
        }
        throw error;
    } finally {
        window.clearTimeout(timeoutId);
    }
};

export const readDirectProductCurrentUser = (productOrigin: string): Promise<CurrentUserSession | null> => {
    return executeDirectProductRequest<CurrentUserSession | null>(productOrigin, 'GET', '/api/Auth/CurrentUser');
};
